import { defineSlotRecipe } from '@chakra-ui/react';
// Dark-glass toast capsule. This recipe deep-merges over Chakra's default
// toast recipe, which sets solid per-type backgrounds (green.solid, ...),
// logical paddings (ps/pe/py) and a description opacity — every one of those
// must be explicitly overridden here or it leaks through the merge.
export const toastRecipe = defineSlotRecipe({
    className: 'chakra-toast',
    slots: [
        'root',
        'closeTrigger',
        'title',
        'description',
        'indicator',
        'actionTrigger',
    ],
    base: {
        root: {
            width: { base: 'calc(100vw - 2rem)', md: '392px' },
            display: 'flex',
            alignItems: 'flex-start',
            position: 'relative',
            gap: 3,
            py: '14px',
            ps: '15px',
            pe: '14px',
            overflow: 'hidden',
            borderRadius: '18px',
            color: '#F7F6F3',
            bg: 'rgba(9, 10, 12, 0.93)',
            border: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.10)',
            // Design-spec exception to the no-shadow rule: toasts float over
            // arbitrary content, the dark glass capsule needs lift against both
            // cream and dark surfaces.
            boxShadow: '0 22px 46px -18px rgba(9, 10, 12, 0.6)',
            backdropFilter: 'blur(24px) saturate(170%)',
            translate: 'var(--x) var(--y)',
            scale: 'var(--scale)',
            zIndex: 'var(--z-index)',
            height: 'var(--height)',
            opacity: 'var(--opacity)',
            willChange: 'translate, opacity, scale',
            transition: 'translate 400ms, scale 400ms, opacity 400ms, height 400ms',
            transitionTimingFunction: 'cubic-bezier(0.21, 1.02, 0.73, 1)',
            animation: 'toast-blur-in 420ms cubic-bezier(0.16, 1, 0.3, 1) both',
            // Lilac is the default accent ("agent" toasts), overridden per type.
            '--toast-accent': '#b9a7ff',
            '--toast-glow': 'rgba(185, 167, 255, 0.24)',
            _closed: {
                transition: 'translate 400ms, scale 400ms, opacity 280ms, height 400ms',
                transitionTimingFunction: 'cubic-bezier(0.06, 0.71, 0.55, 1)',
                animation: 'toast-slide-out 280ms cubic-bezier(0.4, 0, 0.9, 0.3) forwards',
            },
            _before: {
                content: '""',
                position: 'absolute',
                top: '-54px',
                left: '-26px',
                width: '200px',
                height: '130px',
                borderRadius: 'full',
                background: 'radial-gradient(circle, var(--toast-glow), transparent 68%)',
                pointerEvents: 'none',
            },
            '&[data-type=success]': {
                '--toast-accent': '#41d38d',
                '--toast-glow': 'rgba(65, 211, 141, 0.20)',
                bg: 'rgba(9, 10, 12, 0.93)',
                color: '#F7F6F3',
            },
            '&[data-type=error]': {
                '--toast-accent': '#ff6b6b',
                '--toast-glow': 'rgba(255, 107, 107, 0.20)',
                bg: 'rgba(9, 10, 12, 0.93)',
                color: '#F7F6F3',
            },
            '&[data-type=warning]': {
                '--toast-accent': '#f5b84b',
                '--toast-glow': 'rgba(245, 184, 75, 0.20)',
                bg: 'rgba(9, 10, 12, 0.93)',
                color: '#F7F6F3',
            },
            '&[data-type=info]': {
                '--toast-accent': '#7dd3fc',
                '--toast-glow': 'rgba(125, 211, 252, 0.18)',
            },
            '&[data-type=ai]': {
                '--toast-accent': '#b9a7ff',
                '--toast-glow': 'rgba(185, 167, 255, 0.24)',
            },
            '&[data-type=loading]': {
                '--toast-accent': '#74a7ff',
                '--toast-glow': 'rgba(116, 167, 255, 0.18)',
            },
            // Custom indicator markup: Chakra's Toast.Indicator discards children
            // (it only maps success/error/warning to built-in icons), so the
            // dashboard renders its own span tagged data-toast-indicator.
            '& [data-toast-indicator]': {
                position: 'relative',
                flexShrink: 0,
                width: '30px',
                height: '30px',
                display: 'grid',
                placeItems: 'center',
                color: 'var(--toast-accent)',
            },
            // The ring svg is absolutely positioned; lift the icon above it.
            '& [data-toast-indicator] > :not([data-toast-ring])': {
                position: 'relative',
            },
            '& [data-toast-ring]': {
                position: 'absolute',
                inset: 0,
                transform: 'rotate(-90deg)',
            },
            '& [data-toast-ring-track]': {
                stroke: 'rgba(255, 255, 255, 0.13)',
            },
            '& [data-toast-ring-fill]': {
                stroke: 'var(--toast-accent)',
                animationName: 'toast-ring',
                animationTimingFunction: 'linear',
                animationFillMode: 'forwards',
            },
            // Zag flags the root while its dismiss timer is paused (hover, page
            // idle); freezing the ring here keeps it in sync with the real timer.
            '&[data-paused] [data-toast-ring-fill]': {
                animationPlayState: 'paused',
            },
        },
        // Styles Chakra's bare <Toast.Indicator /> (affiliate portal); the
        // dashboard bypasses it, see [data-toast-indicator] in root above.
        indicator: {
            flexShrink: 0,
            width: '18px',
            height: '18px',
            marginTop: '6px',
            color: 'var(--toast-accent)',
        },
        title: {
            fontFamily: 'heading',
            fontSize: '13.5px',
            fontWeight: 'semibold',
            color: '#F7F6F3',
            lineHeight: '1.35',
            marginEnd: '0',
        },
        description: {
            fontSize: '12.5px',
            lineHeight: '1.45',
            color: 'rgba(247, 246, 243, 0.72)',
            opacity: '1',
        },
        actionTrigger: {
            alignSelf: 'flex-start',
            mt: '7px',
            height: '26px',
            px: '11px',
            borderRadius: 'md',
            border: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.16)',
            bg: 'rgba(255, 255, 255, 0.06)',
            fontFamily: 'heading',
            fontSize: '12px',
            fontWeight: 'semibold',
            color: 'var(--toast-accent)',
            _hover: {
                bg: 'rgba(255, 255, 255, 0.12)',
            },
        },
        closeTrigger: {
            position: 'relative',
            top: 'auto',
            insetEnd: 'auto',
            padding: '0',
            flexShrink: 0,
            alignSelf: 'flex-start',
            width: '24px',
            height: '24px',
            minWidth: '24px',
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'md',
            borderWidth: '0',
            color: 'rgba(247, 246, 243, 0.60)',
            bg: 'transparent',
            _icon: {
                boxSize: '14px',
            },
            _hover: {
                bg: 'rgba(255, 255, 255, 0.09)',
                color: '#F7F6F3',
            },
        },
    },
});
